import { createSlice } from '@reduxjs/toolkit';
import { PaginationMetaModel } from '../../../../shared/components/pagination/model/Pagination.model';
import { resetAll } from '../../../../shared/infra/redux/baseActions';
import { ReduxStatus } from '../../../../shared/types';
import { LogDateCountModel } from '../../analytics/models/LogDateCount.model';
import { FeedItemConversionDTO } from '../dtos/FeedItemConversions.dto';
import { FeaturedFeedItemModel } from '../models/FeaturedFeedItem.model';
import { FeedItemActivityLogModel } from '../models/FeedItemActivityLog.model';
import { FeedItemNotificationModel } from '../models/FeedItemNotification.model';
import {
  FeedItemModelPropsUnion,
  FeedItemViewModel,
} from '../models/FeedItemView.model';
import { FeedItemViewershipModel } from '../models/FeedItemViewership.model';
import {
  setPinnedFeedItem,
  unsetPinnedFeedItem,
  getFeedItemView,
  getFeedItemViews,
  getFeedItemViewership,
  getFeedItemNotifications,
  getFeedItemConversions,
  getFeedItemActivity,
  getFeedItemAnalyticData,
  getCommunityPublicFeedItems,
  getFeedItemPublishingData,
} from './operators';
import { FeedItemPublishingLogModel } from '../models/FeedItemPublishingLog.model';

export type AnalyticData = {
  views: LogDateCountModel[];
  viewership: FeedItemViewershipModel;
  conversions: FeedItemConversionDTO;
  notifications: FeedItemNotificationModel[];
  activity: {
    data: FeedItemActivityLogModel[];
    meta: PaginationMetaModel;
  };
  publishing: {
    data: FeedItemPublishingLogModel[];
    meta: PaginationMetaModel;
  };
};

export interface FeedItemState {
  feedItem: FeedItemViewModel<FeedItemModelPropsUnion> | null;
  analyticData: AnalyticData;
  publicFeed: FeaturedFeedItemModel[];
  publicPagination: PaginationMetaModel;
  pinned: FeaturedFeedItemModel | null;
  getCommunityPinnedFeedItem: {
    status: ReduxStatus;
    error: string | null;
  };
  getPublicFeedItemsStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  publishFeedItemStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  setPinnedFeedItemStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  unsetPinnedFeedItemStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  retractFeedItemStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  getFeedItemStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  getFeedItemViewsStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  getFeedItemViewershipStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  getFeedItemNotificationsStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  getFeedItemConversionsStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  getFeedItemActivityStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  getFeedItemPublishingStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  getFeedItemAnalyticDataStatus: {
    status: ReduxStatus;
    error: string | null;
  };
}

const initialState: FeedItemState = {
  feedItem: null,
  analyticData: {
    views: [],
    viewership: {
      totalViews: 0,
      uniqueViews: 0,
      viewership: 0,
    },

    conversions: {
      data: [],
      totalConversions: 0,
    },
    notifications: [],
    activity: {
      data: [],
      meta: {
        current: 0,
        pages: 0,
        total: 0,
      },
    },
    publishing: {
      data: [],
      meta: {
        current: 0,
        pages: 0,
        total: 0,
      },
    },
  },
  publicFeed: [],
  publicPagination: {
    current: 0,
    pages: 0,
    total: 0,
  },
  pinned: null,
  getCommunityPinnedFeedItem: {
    status: 'idle',
    error: null,
  },
  getPublicFeedItemsStatus: {
    status: 'idle',
    error: null,
  },
  publishFeedItemStatus: {
    status: 'idle',
    error: null,
  },
  setPinnedFeedItemStatus: {
    status: 'idle',
    error: null,
  },
  unsetPinnedFeedItemStatus: {
    status: 'idle',
    error: null,
  },
  retractFeedItemStatus: {
    status: 'idle',
    error: null,
  },
  getFeedItemStatus: {
    status: 'idle',
    error: null,
  },
  getFeedItemViewsStatus: {
    status: 'idle',
    error: null,
  },
  getFeedItemViewershipStatus: {
    status: 'idle',
    error: null,
  },
  getFeedItemNotificationsStatus: {
    status: 'idle',
    error: null,
  },
  getFeedItemConversionsStatus: {
    status: 'idle',
    error: null,
  },
  getFeedItemActivityStatus: {
    status: 'idle',
    error: null,
  },
  getFeedItemPublishingStatus: {
    status: 'idle',
    error: null,
  },
  getFeedItemAnalyticDataStatus: {
    status: 'idle',
    error: null,
  },
};

const feedItemSplice = createSlice({
  name: 'feedItem',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getFeedItemView.pending, (state) => {
      state.getFeedItemStatus = {
        status: 'loading',
        error: null,
      };
      state.feedItem = null;
    });
    builder.addCase(getFeedItemView.fulfilled, (state, { payload }) => {
      state.getFeedItemStatus = {
        status: 'success',
        error: null,
      };
      state.feedItem = payload;
    });
    builder.addCase(getFeedItemView.rejected, (state, { payload }) => {
      state.getFeedItemStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to fetch feed item view',
      };
      state.feedItem = null;
    });
    builder.addCase(getFeedItemViews.pending, (state) => {
      state.getFeedItemViewsStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(getFeedItemViews.fulfilled, (state, { payload }) => {
      state.getFeedItemViewsStatus = {
        status: 'success',
        error: null,
      };
      state.analyticData.views = payload;
    });
    builder.addCase(getFeedItemViews.rejected, (state, { payload }) => {
      state.getFeedItemViewsStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to fetch feed item views',
      };
      state.analyticData.views = [];
    });
    builder.addCase(getFeedItemViewership.pending, (state) => {
      state.getFeedItemViewershipStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(getFeedItemViewership.fulfilled, (state, { payload }) => {
      state.getFeedItemViewershipStatus = {
        status: 'success',
        error: null,
      };
      state.analyticData.viewership = payload;
    });
    builder.addCase(getFeedItemViewership.rejected, (state, { payload }) => {
      state.getFeedItemViewershipStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to fetch feed item viewership',
      };
      state.analyticData.viewership = {
        totalViews: 0,
        uniqueViews: 0,
        viewership: 0,
      };
    });
    builder.addCase(getFeedItemNotifications.pending, (state) => {
      state.getFeedItemNotificationsStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(
      getFeedItemNotifications.fulfilled,
      (state, { payload }) => {
        state.getFeedItemNotificationsStatus = {
          status: 'success',
          error: null,
        };
        state.analyticData.notifications = payload;
      },
    );
    builder.addCase(getFeedItemNotifications.rejected, (state, { payload }) => {
      state.getFeedItemNotificationsStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to fetch feed item notifications',
      };
      state.analyticData.notifications = [];
    });
    builder.addCase(getFeedItemConversions.pending, (state) => {
      state.getFeedItemConversionsStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(getFeedItemConversions.fulfilled, (state, { payload }) => {
      state.getFeedItemConversionsStatus = {
        status: 'success',
        error: null,
      };
      state.analyticData.conversions = payload;
    });
    builder.addCase(getFeedItemConversions.rejected, (state, { payload }) => {
      state.getFeedItemConversionsStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to fetch feed item conversions',
      };
      state.analyticData.conversions = { data: [], totalConversions: 0 };
    });

    builder.addCase(getFeedItemActivity.pending, (state) => {
      state.getFeedItemActivityStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(getFeedItemActivity.fulfilled, (state, { payload }) => {
      state.getFeedItemActivityStatus = {
        status: 'success',
        error: null,
      };
      state.analyticData.activity.data = payload.model;
      state.analyticData.activity.meta = payload.meta;
    });
    builder.addCase(getFeedItemActivity.rejected, (state, { payload }) => {
      state.getFeedItemActivityStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to fetch feed item activity',
      };
      state.analyticData.activity.data = [];
      state.analyticData.activity.meta = {
        current: 0,
        pages: 0,
        total: 0,
      };
    });

    builder.addCase(getFeedItemPublishingData.pending, (state) => {
      state.getFeedItemPublishingStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(
      getFeedItemPublishingData.fulfilled,
      (state, { payload }) => {
        state.getFeedItemPublishingStatus = {
          status: 'success',
          error: null,
        };
        state.analyticData.publishing.data = payload.model;
        state.analyticData.publishing.meta = payload.meta;
      },
    );
    builder.addCase(
      getFeedItemPublishingData.rejected,
      (state, { payload }) => {
        state.getFeedItemPublishingStatus = {
          status: 'failure',
          error: payload?.message
            ? payload.message
            : 'Error trying to fetch feed item publishing activity',
        };
        state.analyticData.publishing.data = [];
        state.analyticData.publishing.meta = {
          current: 0,
          pages: 0,
          total: 0,
        };
      },
    );

    builder.addCase(getFeedItemAnalyticData.pending, (state) => {
      state.getFeedItemAnalyticDataStatus = {
        status: 'loading',
        error: null,
      };
      state.analyticData = {
        activity: {
          data: [],
          meta: {
            current: 0,
            pages: 0,
            total: 0,
          },
        },
        publishing: {
          data: [],
          meta: {
            current: 0,
            pages: 0,
            total: 0,
          },
        },
        conversions: { data: [], totalConversions: 0 },
        notifications: [],
        viewership: { totalViews: 0, uniqueViews: 0, viewership: 0 },
        views: [],
      };
    });
    builder.addCase(getFeedItemAnalyticData.fulfilled, (state, { payload }) => {
      state.getFeedItemAnalyticDataStatus = {
        status: 'success',
        error: null,
      };
      state.analyticData = {
        views: payload.views ? payload.views : [],
        viewership: payload.viewership
          ? payload.viewership
          : { totalViews: 0, uniqueViews: 0, viewership: 0 },
        activity: {
          data: payload.activity ? payload.activity.model : [],
          meta: payload.activity
            ? payload.activity.meta
            : {
                current: 0,
                pages: 0,
                total: 0,
              },
        },
        publishing: {
          data: payload.publishing ? payload.publishing.model : [],
          meta: payload.publishing
            ? payload.publishing.meta
            : {
                current: 0,
                pages: 0,
                total: 0,
              },
        },
        conversions: payload.conversions
          ? payload.conversions
          : { data: [], totalConversions: 0 },
        notifications: payload.notifications ? payload.notifications : [],
      };
    });
    builder.addCase(getFeedItemAnalyticData.rejected, (state, { payload }) => {
      state.getFeedItemActivityStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to fetch feed item analytics',
      };
      state.analyticData = {
        activity: {
          data: [],
          meta: {
            current: 0,
            pages: 0,
            total: 0,
          },
        },
        publishing: {
          data: [],
          meta: {
            current: 0,
            pages: 0,
            total: 0,
          },
        },
        conversions: {
          data: [],
          totalConversions: 0,
        },
        notifications: [],
        viewership: { totalViews: 0, uniqueViews: 0, viewership: 0 },
        views: [],
      };
    });
    builder.addCase(getCommunityPublicFeedItems.pending, (state) => {
      state.getPublicFeedItemsStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(
      getCommunityPublicFeedItems.fulfilled,
      (state, { payload }) => {
        state.getPublicFeedItemsStatus = {
          status: 'success',
          error: null,
        };
        state.publicFeed = payload.model;
        state.publicPagination = payload.meta;
      },
    );
    builder.addCase(
      getCommunityPublicFeedItems.rejected,
      (state, { payload }) => {
        state.getPublicFeedItemsStatus = {
          status: 'failure',
          error: payload?.message
            ? payload.message
            : 'Error trying to fetch public feed items',
        };
      },
    );
    builder.addCase(setPinnedFeedItem.pending, (state) => {
      state.setPinnedFeedItemStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(setPinnedFeedItem.fulfilled, (state) => {
      state.setPinnedFeedItemStatus = {
        status: 'success',
        error: null,
      };
    });
    builder.addCase(setPinnedFeedItem.rejected, (state, { payload }) => {
      state.setPinnedFeedItemStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to set pinned feed item',
      };
    });
    builder.addCase(unsetPinnedFeedItem.pending, (state) => {
      state.unsetPinnedFeedItemStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(unsetPinnedFeedItem.fulfilled, (state) => {
      state.unsetPinnedFeedItemStatus = {
        status: 'success',
        error: null,
      };
    });
    builder.addCase(unsetPinnedFeedItem.rejected, (state, { payload }) => {
      state.unsetPinnedFeedItemStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to unpin feed item',
      };
    });
    builder.addCase(resetAll, () => initialState);
  },
});

export default feedItemSplice.reducer;
